/**
 * Minimal structural types for the slice of WebCodecs the listener uses.
 *
 * These are deliberately local rather than a `declare global` or an ambient
 * `@types/dom-webcodecs`: TypeScript's own `lib.dom` has grown parts of WebCodecs
 * over time, and a global redeclaration would start colliding on some TS versions.
 * Reading the constructors off `globalThis` also makes the feature test honest —
 * there is no way to reference something that is not there.
 */

export interface AudioDataCopyToOptions {
  planeIndex: number;
  format?: string;
  frameOffset?: number;
  frameCount?: number;
}

export interface AudioDataLike {
  readonly numberOfChannels: number;
  readonly numberOfFrames: number;
  readonly sampleRate: number;
  readonly format: string | null;
  allocationSize(options: AudioDataCopyToOptions): number;
  copyTo(destination: ArrayBufferView, options: AudioDataCopyToOptions): void;
  close(): void;
}

export interface AudioDecoderConfigLike {
  codec: string;
  sampleRate: number;
  numberOfChannels: number;
  description?: ArrayBufferView | ArrayBuffer;
}

export interface AudioDecoderLike {
  readonly state: 'unconfigured' | 'configured' | 'closed';
  readonly decodeQueueSize: number;
  configure(config: AudioDecoderConfigLike): void;
  decode(chunk: EncodedAudioChunkLike): void;
  flush(): Promise<void>;
  reset(): void;
  close(): void;
}

export interface EncodedAudioChunkLike {
  readonly timestamp: number;
}

export interface EncodedAudioChunkInit {
  type: 'key' | 'delta';
  timestamp: number;
  duration?: number;
  data: ArrayBufferView | ArrayBuffer;
}

interface AudioDecoderConstructor {
  new (init: {
    output: (data: AudioDataLike) => void;
    error: (error: DOMException) => void;
  }): AudioDecoderLike;
  isConfigSupported(config: AudioDecoderConfigLike): Promise<{ supported?: boolean }>;
}

interface EncodedAudioChunkConstructor {
  new (init: EncodedAudioChunkInit): EncodedAudioChunkLike;
}

interface WebCodecsGlobals {
  AudioDecoder?: AudioDecoderConstructor;
  EncodedAudioChunk?: EncodedAudioChunkConstructor;
}

export function getWebCodecs(): {
  AudioDecoder: AudioDecoderConstructor;
  EncodedAudioChunk: EncodedAudioChunkConstructor;
} | null {
  const scope = globalThis as unknown as WebCodecsGlobals;
  const AudioDecoder = scope.AudioDecoder;
  const EncodedAudioChunk = scope.EncodedAudioChunk;
  if (typeof AudioDecoder !== 'function' || typeof EncodedAudioChunk !== 'function') return null;
  if (typeof AudioDecoder.isConfigSupported !== 'function') return null;
  return { AudioDecoder, EncodedAudioChunk };
}
